/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * React Query Configuration Constants
 *
 * Centralized configuration for consistent cache management across the application.
 * Use these constants to ensure uniform staleTime and gcTime values.
 */

// ============================================================================
// PAGINATION DEFAULTS
// ============================================================================

/** Shared default page size — used by server components AND client hooks */
export const DEFAULT_PAGE_SIZE = 25;

// ============================================================================
// STALE TIME CONSTANTS (How long data is considered fresh)
// ============================================================================

export const QUERY_STALE_TIME = {
  /** Critical data that changes frequently (5 minutes) */
  CRITICAL: 5 * 60 * 1000, // 5 minutes
  /** Standard data that changes occasionally (10 minutes) */
  STANDARD: 10 * 60 * 1000, // 10 minutes
  /** Stable data that rarely changes (15 minutes) */
  STABLE: 15 * 60 * 1000, // 15 minutes
  /** Very stable data like configurations (20 minutes) */
  VERY_STABLE: 20 * 60 * 1000, // 20 minutes
  /** Performance optimization: Longer staleTime for work-areas (10 minutes) */
  WORK_AREAS: 10 * 60 * 1000, // 10 minutes
} as const;

// ============================================================================
// GARBAGE COLLECTION TIME (How long data stays in cache after unused)
// ============================================================================

export const QUERY_GC_TIME = {
  /** Garbage collection for critical data (10 minutes — 2x staleTime) */
  CRITICAL: 10 * 60 * 1000,
  /** Garbage collection for standard data (20 minutes — 2x staleTime) */
  STANDARD: 20 * 60 * 1000,
  /** Garbage collection for stable data (30 minutes — 2x staleTime) */
  STABLE: 30 * 60 * 1000,
  /** Garbage collection for very stable data (40 minutes — 2x staleTime) */
  VERY_STABLE: 40 * 60 * 1000,
  /** Garbage collection for work-areas (20 minutes — 2x staleTime) */
  WORK_AREAS: 20 * 60 * 1000,
} as const;

// ============================================================================
// DATA TYPE MAPPINGS (Which staleTime to use for different data types)
// ============================================================================

export const DATA_TYPE_STALE_TIME = {
  // Critical - changes frequently
  estimates: QUERY_STALE_TIME.CRITICAL,
  "estimate-details": QUERY_STALE_TIME.CRITICAL,
  "estimate-line-items": QUERY_STALE_TIME.CRITICAL,
  "line-items": QUERY_STALE_TIME.CRITICAL,
  dashboard: QUERY_STALE_TIME.CRITICAL,
  notifications: QUERY_STALE_TIME.CRITICAL,

  // Standard - changes occasionally
  projects: QUERY_STALE_TIME.STANDARD,
  "project-details": QUERY_STALE_TIME.STANDARD,
  clients: QUERY_STALE_TIME.STANDARD,
  "client-details": QUERY_STALE_TIME.STANDARD,
  budgets: QUERY_STALE_TIME.STANDARD,
  "budget-details": QUERY_STALE_TIME.STANDARD,
  suppliers: QUERY_STALE_TIME.STANDARD,
  materials: QUERY_STALE_TIME.STANDARD,
  "labor-rates": QUERY_STALE_TIME.STANDARD,
  equipment: QUERY_STALE_TIME.STANDARD,
  proposals: QUERY_STALE_TIME.STANDARD,
  "work-areas": QUERY_STALE_TIME.WORK_AREAS, // 5 minutes (reduced refetches)
  "takeoff-work-areas": QUERY_STALE_TIME.WORK_AREAS, // 5 minutes
  "work-areas-lookup": QUERY_STALE_TIME.WORK_AREAS, // 5 minutes

  // Stable - rarely changes
  "organization-users": QUERY_STALE_TIME.STABLE,
  divisions: QUERY_STALE_TIME.STABLE,
  "cost-profiles": QUERY_STALE_TIME.STABLE,
  contacts: QUERY_STALE_TIME.STABLE,
  jobsites: QUERY_STALE_TIME.STABLE,
  documents: QUERY_STALE_TIME.STABLE,
  templates: QUERY_STALE_TIME.STABLE,
  "organization-templates": QUERY_STALE_TIME.STABLE,

  // Very stable - configurations
  "user-preferences": QUERY_STALE_TIME.VERY_STABLE,
  "saved-views": QUERY_STALE_TIME.VERY_STABLE,
  analytics: QUERY_STALE_TIME.STABLE, // Can be stale longer
} as const;

// ============================================================================
// RETRY CONFIGURATION
// ============================================================================

export const QUERY_RETRY = {
  /** No retries (for mutations or critical failures) */
  NONE: 0,
  /** Single retry attempt */
  ONCE: 1,
  /** Default retry (2 attempts, matches QueryProvider default) */
  DEFAULT: 2,
  /** Aggressive retry for critical data (3 attempts) */
  AGGRESSIVE: 3,
} as const;

/**
 * Standard retry delay function (exponential backoff with max 30s)
 * Matches QueryProvider default configuration
 */
export function getRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30000);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get appropriate staleTime for a query key
 */
export function getStaleTimeForQueryKey(
  queryKey: string | readonly unknown[],
): number {
  const key =
    typeof queryKey === "string" ? queryKey : String(queryKey[0] ?? "");

  // Check exact match first
  if (key in DATA_TYPE_STALE_TIME) {
    return DATA_TYPE_STALE_TIME[key as keyof typeof DATA_TYPE_STALE_TIME];
  }

  // Check partial matches for nested keys
  for (const [dataType, staleTime] of Object.entries(DATA_TYPE_STALE_TIME)) {
    if (key.includes(dataType)) {
      return staleTime;
    }
  }

  // Default to standard
  return QUERY_STALE_TIME.STANDARD;
}

/**
 * Get appropriate gcTime for a query key (always 2x staleTime)
 */
export function getGcTimeForQueryKey(
  queryKey: string | readonly unknown[],
): number {
  const staleTime = getStaleTimeForQueryKey(queryKey);
  return staleTime * 2;
}

/**
 * Get query config for a specific data type
 */
export function getQueryConfig(dataType: keyof typeof DATA_TYPE_STALE_TIME): {
  staleTime: number;
  gcTime: number;
} {
  const staleTime = DATA_TYPE_STALE_TIME[dataType];
  return {
    staleTime,
    gcTime: staleTime * 2,
  };
}
